import { useMemo } from 'react';
import LocationImage from '../LocationImage';
import { getString } from './helpers';
import { Camera, MapPin } from 'lucide-react';

const GALLERY_QUERIES = [
  { suffix: 'landmark', label: 'Landmarks' },
  { suffix: 'temple monument', label: 'Heritage' },
  { suffix: 'skyline cityscape', label: 'Skyline' },
  { suffix: 'market street food', label: 'Local Life' },
  { suffix: 'park garden nature', label: 'Nature' },
  { suffix: 'beach waterfront', label: 'Waterfront' },
  { suffix: 'museum architecture', label: 'Architecture' },
  { suffix: 'night city lights', label: 'Nightlife' },
];

export const GalleryTab = ({ trip_input, generatedItinerary }) => {
  const itineraryPlaces = useMemo(() => {
    if (!generatedItinerary.days) return [];
    return generatedItinerary.days
      .flatMap((day) => ['morning', 'afternoon', 'evening'].map(t => day[t]).filter(Boolean))
      .slice(0, 6)
      .map((activity) => {
        const loc = typeof activity === 'object' ? (activity.location || activity.activity || '') : activity;
        return typeof loc === 'string' ? loc : '';
      });
  }, [generatedItinerary.days]);

  return (
    <div className="space-y-6" data-testid="gallery-tab-content">
      <h3 className="font-serif text-xl text-ocean flex items-center gap-2">
        <Camera className="w-5 h-5 text-gold" />
        Destination Gallery
      </h3>
      
      {(trip_input.destination_cities?.length > 1 ? trip_input.destination_cities : [trip_input.destination_city]).map((city, cityIdx) => (
        <div key={city || cityIdx} className="mb-8">
          <h4 className="font-serif text-lg text-ocean mb-4 flex items-center gap-2">
            <MapPin className="w-4 h-4 text-gold" />
            {city}
          </h4>
          {/* Main gallery grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {GALLERY_QUERIES.slice(0, 8).map((q, imgIdx) => (
              <div
                key={`${city}-${q.suffix}`}
                className="relative group overflow-hidden rounded-xl shadow-lg aspect-square cursor-pointer bg-sky/5"
                data-testid={`gallery-image-${cityIdx}-${imgIdx}`}
              >
                <LocationImage
                  query={`${city} ${q.suffix}`}
                  type="activity"
                  index={imgIdx + cityIdx * 100}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  alt={`${city} - ${q.label}`}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ocean/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-3">
                  <p className="text-white text-sm font-medium">{city} - {q.label}</p>
                </div>
              </div>
            ))}
          </div>
          
          {/* Places from itinerary */}
          {itineraryPlaces.length > 0 && (
            <div className="mt-6">
              <p className="text-sm text-ocean/60 mb-3 font-medium">Places from Your Itinerary</p>
              <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                {itineraryPlaces.map((locStr, actIdx) => (
                  <div key={`place-${actIdx}-${locStr.slice(0, 20)}`} className="relative overflow-hidden rounded-lg aspect-square bg-sky/5">
                    <LocationImage
                      query={locStr.split(',')[0]?.split('.')[0]?.trim() || city}
                      type="activity"
                      index={actIdx + cityIdx * 10 + 200}
                      className="w-full h-full object-cover"
                      alt={locStr || city}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent flex items-end p-1.5">
                      <p className="text-white text-[10px] leading-tight line-clamp-2">{locStr.split('.')[0]?.trim() || city}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};
